import React, { useEffect } from 'react';
import { Autocomplete, Box, Button, Grid, IconButton, TextField, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { Controller } from 'react-hook-form';

import { useStorageAreaForm } from 'hooks/Warehouse';

const StorageAreaForm = (props) => {
  const { formId, locationOptions = [], onSubmittingChange } = props;
  const { control, fields, addArea, removeArea, isUniqueAreaName, watch, submit, submitError, submitting } =
    useStorageAreaForm(props);
  const areas = watch('areas');

  useEffect(() => {
    onSubmittingChange?.(submitting);
  }, [submitting, onSubmittingChange]);

  const labelFor = (id) => {
    const location = locationOptions.find((option) => option.id === id);
    return location?.name || id;
  };
  const assignedIds = new Set((areas ?? []).flatMap((area) => area.locations ?? []));
  const assignedCount = locationOptions.filter((location) => assignedIds.has(location.id)).length;
  const locationIds = locationOptions.map((location) => location.id);

  return (
    <Box component="form" id={formId} onSubmit={submit} noValidate>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="caption" color="textSecondary">
          Days of the week is the standard organization.
        </Typography>
        {locationOptions.length > 0 && (
          <Typography variant="caption" color="textSecondary">
            {`${assignedCount} of ${locationOptions.length} locations assigned · ${
              locationOptions.length - assignedCount
            } unassigned`}
          </Typography>
        )}
      </Box>
      <Box sx={{ mt: 1 }}>
        {fields.length > 0 && (
          <Grid container spacing={1} sx={{ mb: 0.5 }}>
            <Grid size={4}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Area Name
              </Typography>
            </Grid>
            <Grid size={7}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Locations
              </Typography>
            </Grid>
            <Grid size={1} />
          </Grid>
        )}
        {fields.map((row, index) => (
          <Grid container spacing={1} key={row.key} sx={{ alignItems: 'flex-start', mb: 1 }}>
            <Grid size={4}>
              <Controller
                name={`areas.${index}.name`}
                control={control}
                rules={{ required: 'Storage area names must not be empty', validate: isUniqueAreaName }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    variant="outlined"
                    size="small"
                    fullWidth
                    placeholder="Storage area name"
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message}
                    slotProps={{ htmlInput: { 'aria-label': `storage-area-name-${index}` } }}
                  />
                )}
              />
            </Grid>
            <Grid size={7}>
              <Controller
                name={`areas.${index}.locations`}
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    multiple
                    size="small"
                    limitTags={3}
                    options={locationIds}
                    filterSelectedOptions
                    getOptionLabel={labelFor}
                    filterOptions={(options, { inputValue }) => {
                      const query = inputValue.trim().toLowerCase();
                      return options.filter(
                        (id) => id.toLowerCase().includes(query) || labelFor(id).toLowerCase().includes(query),
                      );
                    }}
                    value={field.value || []}
                    onChange={(_, value) => field.onChange(value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder={(field.value || []).length ? '' : 'Search locations…'}
                        slotProps={{
                          ...params.slotProps,
                          htmlInput: {
                            ...params.slotProps.htmlInput,
                            'aria-label': `storage-area-locations-${index}`,
                          },
                        }}
                      />
                    )}
                  />
                )}
              />
            </Grid>
            <Grid size={1}>
              <IconButton aria-label={`remove-storage-area-${index}`} onClick={() => removeArea(index)} size="small">
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Grid>
          </Grid>
        ))}
        <Button variant="outlined" onClick={addArea}>
          Add storage area
        </Button>
      </Box>
      {submitError && (
        <Typography color="error" variant="body2">
          {submitError}
        </Typography>
      )}
    </Box>
  );
};

export default StorageAreaForm;
